import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useState } from 'react';
import { DetailViewKey, ViewKey } from './GetRenderView';
import {
  Detail1ResponseType,
  MedicalHealthInformationData,
  DetailItem,
  QuestionnaireResponse,
  DocumentReference,
  MediaImage,
  MediaVideo,
  FhirId
} from './types/MedicalHealthInformation';
import { fetchAuthSession } from 'aws-amplify/auth';
import { NoListError } from './errors/errors';
import {
  MEDICAL_HEALTH_INFORMATION_BRANCH_QUESTIONNAIRE_RESPONSE_NAME,
  MEDICAL_HEALTH_INFORMATION_NAME
} from './constants/constants';

import { executeGet } from '@/client/rest';

// detail1のリクエストで使用するモード(マスキングあり・なし)
const DETAIL1_REQUEST_MODE = {
  MASK: 'MASK', // マスキングあり
  UNMASK: 'UNMASK' // マスキングなし
} as const;

const defaultActiveMedicalHealthInformationData: MedicalHealthInformationData = {
  facility_id: 0,
  facility_name: '',
  reserve_id: 0,
  outpatient_id: '',
  outpatient_name: '',
  reserve_date: '',
  reserve_time: '',
  questionnaire_response_list: [],
  observation_list: [],
  condition_list: [],
  document_reference_list: [],
  media_image_list: [],
  media_video_list: []
};

const defaultActiveQuestionnaireResponse: QuestionnaireResponse = {
  id: '',
  information_kbn: '',
  complete_flag: ''
};

const defaultDetailDocumentReference: DocumentReference = {
  id: '',
  name: '',
  url: '',
  is_masked: false
};

const defaultDetailMediaImage: MediaImage = {
  id: '',
  content_type: '',
  url: '',
  is_masked: false
};

const defaultDetailMediaVideo: MediaVideo = {
  id: '',
  content_type: '',
  url: '',
  is_masked: false
};

type ContextType = {
  // userId
  cognitoId: string;

  // ページ管理
  activeStep: ViewKey;
  setActiveStep: Dispatch<SetStateAction<ViewKey>>;

  // 詳細画面管理
  detailStep: DetailViewKey;
  setDetailStep: Dispatch<SetStateAction<DetailViewKey>>;

  // 施設名
  facilityName: string;
  setFacilityName: Dispatch<SetStateAction<string>>;

  // fetcher
  // 一覧
  fetchMedicalHealthInformationList: () => Promise<void>;
  // 詳細 isUnmaskでマスキングあり・なしを制御
  fetchMedicalHealthInformationDetail1: (fhirId: FhirId, facilityId: string, isUnmask: boolean) => Promise<void>;
  // 詳細(資料) マスキング解除
  fetchMedicalHealthInformationDetail1ForDocumentReference: (fhirId: FhirId, facilityId: string) => Promise<void>;
  // 詳細(画像) マスキング解除 1枚
  fetchMedicalHealthInformationDetail1ForMediaImage: (fhirId: FhirId, facilityId: string) => Promise<void>;
  // 詳細(画像) マスキング解除 全て
  fetchMedicalHealthInformationDetail1ForMediaImageAll: (
    fhirId: FhirId,
    facilityId: string
  ) => Promise<Detail1ResponseType['media_image']>;
  // 詳細(動画) マスキング解除 1枚
  fetchMedicalHealthInformationDetail1ForMediaVideo: (fhirId: FhirId, facilityId: string) => Promise<void>;
  // 詳細(動画) マスキング解除 全て
  fetchMedicalHealthInformationDetail1ForMediaVideoAll: (
    fhirId: FhirId,
    facilityId: string
  ) => Promise<Detail1ResponseType['media_video']>;

  // 一覧
  medicalHealthInformationList: MedicalHealthInformationData[];
  // アクティブな詳細
  activeMedicalHealthInformation: MedicalHealthInformationData;
  setActiveMedicalHealthInformation: Dispatch<SetStateAction<MedicalHealthInformationData>>;
  // マスク解除に使用するfhir id
  fhirIdForUnmask: FhirId | null;
  setFhirIdForUnmask: Dispatch<SetStateAction<FhirId | null>>;
  // アクティブなQuestionnaireResponse
  activeQuestionnaireResponse: QuestionnaireResponse;
  setActiveQuestionnaireResponse: Dispatch<SetStateAction<QuestionnaireResponse>>;
  // 詳細の項目一覧
  detailItemList: DetailItem[];
  // 詳細の資料
  detailDocumentReference: DocumentReference;
  setDetailDocumentReference: Dispatch<SetStateAction<DocumentReference>>;
  // 詳細の画像
  detailMediaImage: MediaImage;
  setDetailMediaImage: Dispatch<SetStateAction<MediaImage>>;
  // 詳細の動画
  detailMediaVideo: MediaVideo;
  setDetailMediaVideo: Dispatch<SetStateAction<MediaVideo>>;
};

const Context = createContext<ContextType | null>(null);

export const MedicalHealthInformationContextProvider = ({ children }: { children: ReactNode }) => {
  const [cognitoId, setCognitoId] = useState('');
  const [activeStep, setActiveStep] = useState<ViewKey>(MEDICAL_HEALTH_INFORMATION_NAME);
  const [detailStep, setDetailStep] = useState<DetailViewKey>(
    MEDICAL_HEALTH_INFORMATION_BRANCH_QUESTIONNAIRE_RESPONSE_NAME
  );
  const [facilityName, setFacilityName] = useState('');
  const [medicalHealthInformationList, setMedicalHealthInformationList] = useState<MedicalHealthInformationData[]>([]);
  const [activeMedicalHealthInformation, setActiveMedicalHealthInformation] = useState<MedicalHealthInformationData>(
    defaultActiveMedicalHealthInformationData
  );
  const [activeQuestionnaireResponse, setActiveQuestionnaireResponse] = useState<QuestionnaireResponse>(
    defaultActiveQuestionnaireResponse
  );
  const [fhirIdForUnmask, setFhirIdForUnmask] = useState<FhirId | null>(null);
  const [detailItemList, setDetailItemList] = useState<DetailItem[]>([]);
  const [detailDocumentReference, setDetailDocumentReference] =
    useState<DocumentReference>(defaultDetailDocumentReference);
  const [detailMediaImage, setDetailMediaImage] = useState<MediaImage>(defaultDetailMediaImage);
  const [detailMediaVideo, setDetailMediaVideo] = useState<MediaVideo>(defaultDetailMediaVideo);

  /**
   * 一覧取得
   */
  const fetchMedicalHealthInformationList = useCallback(async () => {
    const session = await fetchAuthSession();
    const idtoken = session.tokens?.idToken?.toString();
    const userSub = session.userSub;
    if (!idtoken || !userSub) return;

    const result = await executeGet<MedicalHealthInformationData[]>({
      apiName: 'medirecoWebApi',
      path: '/medical/health-info/list',
      options: {
        headers: {
          authorization: idtoken
        }
      }
    });
    if (result.length === 0) {
      throw new NoListError();
    }
    setMedicalHealthInformationList(result);
    setCognitoId(userSub);
  }, []);

  /**
   * 詳細1取得
   */
  const fetchMedicalHealthInformationDetail1 = useCallback(
    async (fhirId: FhirId, facilityId: string, isUnmasking: boolean) => {
      const session = await fetchAuthSession({ forceRefresh: true }); // 毎回refreshが必要
      const idtoken = session.tokens?.idToken?.toString();
      if (!idtoken) return;

      const data = await executeGet<Detail1ResponseType>({
        apiName: 'medirecoWebApi',
        path: '/medical/health-info/detail1',
        options: {
          queryParams: {
            fhirId,
            facilityId,
            mode: isUnmasking ? DETAIL1_REQUEST_MODE.UNMASK : DETAIL1_REQUEST_MODE.MASK
          },
          headers: {
            authorization: idtoken
          }
        }
      });
      setDetailItemList(data?.information_list ?? []);
    },
    []
  );

  /**
   * 詳細1取得(資料)
   */
  const fetchMedicalHealthInformationDetail1ForDocumentReference = useCallback(
    async (fhirId: FhirId, facilityId: string) => {
      const session = await fetchAuthSession({ forceRefresh: true }); // 毎回refreshが必要
      const idtoken = session.tokens?.idToken?.toString();
      if (!idtoken) return;

      const data = await executeGet<Detail1ResponseType>({
        apiName: 'medirecoWebApi',
        path: '/medical/health-info/detail1',
        options: {
          queryParams: { fhirId, facilityId, mode: DETAIL1_REQUEST_MODE.UNMASK },
          headers: {
            authorization: idtoken
          }
        }
      });
      if (data?.document_reference) {
        setDetailDocumentReference(data.document_reference);
      }
    },
    []
  );

  /**
   * 詳細1取得(画像)
   */
  const fetchMedicalHealthInformationDetail1ForMediaImage = useCallback(async (fhirId: FhirId, facilityId: string) => {
    const res = await fetchMedicalHealthInformationDetail1ForMediaImageAll(fhirId, facilityId);
    if (res) {
      setDetailMediaImage(res);
    }
  }, []);

  /**
   * 詳細1取得(画像)
   * 中でセッターを使わずにレスポンスを返している
   */
  const fetchMedicalHealthInformationDetail1ForMediaImageAll = useCallback(
    async (fhirId: FhirId, facilityId: string) => {
      const session = await fetchAuthSession({ forceRefresh: true }); // 毎回refreshが必要
      const idtoken = session.tokens?.idToken?.toString();
      if (!idtoken) return;

      const data = await executeGet<Detail1ResponseType>({
        apiName: 'medirecoWebApi',
        path: '/medical/health-info/detail1',
        options: {
          queryParams: { fhirId, facilityId, mode: DETAIL1_REQUEST_MODE.UNMASK },
          headers: {
            authorization: idtoken
          }
        }
      });
      if (data?.document_reference) {
        return data.document_reference;
      }
    },
    []
  );

  /**
   * 詳細1取得(動画)
   */
  const fetchMedicalHealthInformationDetail1ForMediaVideo = useCallback(async (fhirId: FhirId, facilityId: string) => {
    const res = await fetchMedicalHealthInformationDetail1ForMediaVideoAll(fhirId, facilityId);
    if (res) {
      setDetailMediaVideo(res);
    }
  }, []);

  /**
   * 詳細1取得(動画)
   * 中でセッターを使わずにレスポンスを返している
   */
  const fetchMedicalHealthInformationDetail1ForMediaVideoAll = useCallback(
    async (fhirId: FhirId, facilityId: string) => {
      const session = await fetchAuthSession({ forceRefresh: true }); // 毎回refreshが必要
      const idtoken = session.tokens?.idToken?.toString();
      if (!idtoken) return;

      const data = await executeGet<Detail1ResponseType>({
        apiName: 'medirecoWebApi',
        path: '/medical/health-info/detail1',
        options: {
          queryParams: { fhirId, facilityId, mode: DETAIL1_REQUEST_MODE.UNMASK },
          headers: {
            authorization: idtoken
          }
        }
      });

      if (data?.document_reference) {
        return data.document_reference;
      }
    },
    []
  );

  const value = {
    cognitoId,
    activeStep,
    setActiveStep,
    detailStep,
    setDetailStep,
    facilityName,
    setFacilityName,
    fetchMedicalHealthInformationList,
    fetchMedicalHealthInformationDetail1,
    fetchMedicalHealthInformationDetail1ForDocumentReference,
    fetchMedicalHealthInformationDetail1ForMediaImage,
    fetchMedicalHealthInformationDetail1ForMediaImageAll,
    fetchMedicalHealthInformationDetail1ForMediaVideo,
    fetchMedicalHealthInformationDetail1ForMediaVideoAll,
    medicalHealthInformationList,
    activeMedicalHealthInformation,
    setActiveMedicalHealthInformation,
    activeQuestionnaireResponse,
    setActiveQuestionnaireResponse,
    fhirIdForUnmask,
    setFhirIdForUnmask,
    detailItemList,
    detailDocumentReference,
    setDetailDocumentReference,
    detailMediaImage,
    setDetailMediaImage,
    detailMediaVideo,
    setDetailMediaVideo
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useMedicalHealthInformationContext = () => {
  const context = useContext(Context);
  if (!context) throw new Error('no context');

  return context;
};
